import React, { useState, useEffect } from 'react';
import { Modal, Button, Table, Form, Alert, Badge, Spinner, InputGroup } from 'react-bootstrap';
import { FaSearch, FaUndo, FaCheckCircle, FaExclamationTriangle, FaChalkboardTeacher } from 'react-icons/fa';

const RestoreDeletedTeachersModal = ({ 
  show, 
  onHide, 
  schoolid,
  teacherid,
  teacherName,
  getDeletedTeachersBySchool,
  restoreTeacher,
  batchRestoreTeachers
}) => {
  const [deletedTeachers, setDeletedTeachers] = useState([]);
  const [filteredTeachers, setFilteredTeachers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedTeachers, setSelectedTeachers] = useState(new Set());
  const [searchTerm, setSearchTerm] = useState('');
  const [includeRestored, setIncludeRestored] = useState(false);
  const [timeFilter, setTimeFilter] = useState('all');
  const [restoring, setRestoring] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [showConfirmation, setShowConfirmation] = useState(false);

  useEffect(() => {
    if (show && schoolid) {
      loadDeletedTeachers();
    }
  }, [show, schoolid, includeRestored]);

  useEffect(() => {
    filterTeachers();
  }, [deletedTeachers, searchTerm, timeFilter]);

  const loadDeletedTeachers = async () => {
    setLoading(true);
    setMessage({ type: '', text: '' });
    try {
      const response = await getDeletedTeachersBySchool(schoolid, includeRestored, 500, 0);
      setDeletedTeachers(response.data || []);
    } catch (error) {
      setMessage({ type: 'danger', text: `Failed to load deleted teachers: ${error.message}` });
    } finally {
      setLoading(false);
    }
  };

  const filterTeachers = () => {
    let filtered = [...deletedTeachers];
    
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(teacher => 
        teacher.name?.toLowerCase().includes(search) ||
        teacher.email?.toLowerCase().includes(search) ||
        teacher.id?.toLowerCase().includes(search)
      );
    }

    if (timeFilter !== 'all') {
      const now = new Date();
      const cutoff = new Date();
      
      switch(timeFilter) {
        case 'today':
          cutoff.setHours(0, 0, 0, 0);
          break;
        case 'week':
          cutoff.setDate(now.getDate() - 7);
          break;
        case 'month':
          cutoff.setDate(now.getDate() - 30);
          break;
      }
      
      filtered = filtered.filter(teacher => new Date(teacher.deleted_at) >= cutoff);
    }

    setFilteredTeachers(filtered);
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      const unrestoredIds = filteredTeachers.filter(t => !t.restored_at).map(t => t.id);
      setSelectedTeachers(new Set(unrestoredIds));
    } else {
      setSelectedTeachers(new Set());
    }
  };

  const handleSelectTeacher = (teacherId, isRestored) => {
    if (isRestored) return;
    const newSelected = new Set(selectedTeachers);
    if (newSelected.has(teacherId)) {
      newSelected.delete(teacherId);
    } else {
      newSelected.add(teacherId);
    }
    setSelectedTeachers(newSelected);
  };

  const handleRestore = async () => {
    if (selectedTeachers.size === 0) {
      setMessage({ type: 'warning', text: 'Please select at least one teacher to restore' });
      return;
    }
    setShowConfirmation(true);
  };

  const confirmRestore = async () => {
    setRestoring(true);
    setMessage({ type: '', text: '' });
    setShowConfirmation(false);

    try {
      const teacherIds = Array.from(selectedTeachers);
      
      if (teacherIds.length === 1) {
        await restoreTeacher(teacherIds[0], teacherid, teacherName);
        setMessage({ type: 'success', text: '✓ Teacher restored successfully!' });
      } else {
        const result = await batchRestoreTeachers(schoolid, teacherIds, teacherid, teacherName);
        setMessage({ type: 'success', text: `✓ Successfully restored ${result.restoredCount} teacher(s)!` });
        
        if (result.errors && result.errors.length > 0) {
          setMessage(prev => ({
            ...prev,
            text: prev.text + ` ${result.errors.length} failed.`
          }));
        }
      }

      setSelectedTeachers(new Set());
      await loadDeletedTeachers();
    } catch (error) {
      setMessage({ type: 'danger', text: `Restoration failed: ${error.message}` });
    } finally {
      setRestoring(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
  };

  const getTimeAgo = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${diffDays}d ago`;
  };

  return (
    <>
      <Modal show={show} onHide={onHide} size="xl" backdrop="static">
        <Modal.Header closeButton>
          <Modal.Title>
            <FaChalkboardTeacher className="me-2" />
            Restore Deleted Teachers
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {message.text && (
            <Alert variant={message.type} onClose={() => setMessage({ type: '', text: '' })} dismissible>
              {message.text}
            </Alert>
          )}

          <div className="mb-3">
            <div className="row g-2">
              <div className="col-md-6">
                <InputGroup>
                  <InputGroup.Text><FaSearch /></InputGroup.Text>
                  <Form.Control
                    placeholder="Search by teacher name, email, or ID..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </InputGroup>
              </div>
              <div className="col-md-3">
                <Form.Select value={timeFilter} onChange={(e) => setTimeFilter(e.target.value)}>
                  <option value="all">All Time</option>
                  <option value="today">Deleted Today</option>
                  <option value="week">Last 7 Days</option>
                  <option value="month">Last 30 Days</option>
                </Form.Select>
              </div>
              <div className="col-md-3">
                <Form.Check
                  type="checkbox"
                  label="Show Already Restored"
                  checked={includeRestored}
                  onChange={(e) => setIncludeRestored(e.target.checked)}
                />
              </div>
            </div>
          </div>

          <div className="mb-3 p-2 bg-light rounded">
            <small className="text-muted">
              Showing {filteredTeachers.length} of {deletedTeachers.length} deleted teachers
              {selectedTeachers.size > 0 && ` | ${selectedTeachers.size} selected`}
            </small>
          </div>

          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" />
              <p className="mt-2 text-muted">Loading deleted teachers...</p>
            </div>
          ) : filteredTeachers.length === 0 ? (
            <Alert variant="info">
              <FaCheckCircle className="me-2" />
              No deleted teachers found. {includeRestored ? 'Try adjusting your filters.' : 'All clean!'}
            </Alert>
          ) : (
            <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
              <Table striped bordered hover>
                <thead style={{ position: 'sticky', top: 0, backgroundColor: 'white', zIndex: 1 }}>
                  <tr>
                    <th style={{ width: '50px' }}>
                      <Form.Check
                        type="checkbox"
                        onChange={handleSelectAll}
                        checked={selectedTeachers.size > 0 && 
                          selectedTeachers.size === filteredTeachers.filter(t => !t.restored_at).length}
                      />
                    </th>
                    <th>Teacher Name</th>
                    <th>Email</th>
                    <th>Deleted By</th>
                    <th>Deleted</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredTeachers.map((teacher) => (
                    <tr key={teacher.id} style={{ 
                      opacity: teacher.restored_at ? 0.6 : 1,
                      backgroundColor: teacher.restored_at ? '#f8f9fa' : 'white'
                    }}>
                      <td>
                        <Form.Check
                          type="checkbox"
                          checked={selectedTeachers.has(teacher.id)}
                          onChange={() => handleSelectTeacher(teacher.id, teacher.restored_at)}
                          disabled={!!teacher.restored_at}
                        />
                      </td>
                      <td>
                        <strong>{teacher.name || 'Unnamed Teacher'}</strong>
                        <br />
                        <small className="text-muted">ID: {teacher.id}</small>
                      </td>
                      <td>{teacher.email || 'N/A'}</td>
                      <td>
                        {teacher.deleted_by_teacher_name || 'Unknown'}
                        <br />
                        <small className="text-muted">{teacher.deleted_by_teacher_id}</small>
                      </td>
                      <td>
                        {formatDate(teacher.deleted_at)}
                        <br />
                        <Badge bg="secondary">{getTimeAgo(teacher.deleted_at)}</Badge>
                      </td>
                      <td>
                        {teacher.restored_at ? (
                          <Badge bg="success">
                            <FaCheckCircle className="me-1" />
                            Restored
                          </Badge>
                        ) : (
                          <Badge bg="warning">
                            <FaExclamationTriangle className="me-1" />
                            Deleted
                          </Badge>
                        )}
                        {teacher.restored_at && (
                          <div className="mt-1">
                            <small className="text-muted">
                              by {teacher.restored_by_teacher_name}
                              <br />
                              {formatDate(teacher.restored_at)}
                            </small>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </Modal.Body>

        <Modal.Footer>
          <div className="w-100 d-flex justify-content-between align-items-center">
            <div>
              {selectedTeachers.size > 0 && (
                <span className="text-muted">
                  {selectedTeachers.size} teacher(s) selected for restoration
                </span>
              )}
            </div>
            <div>
              <Button variant="secondary" onClick={onHide} className="me-2">
                Close
              </Button>
              <Button 
                variant="primary" 
                onClick={handleRestore}
                disabled={selectedTeachers.size === 0 || restoring}
              >
                {restoring ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Restoring...
                  </>
                ) : (
                  <>
                    <FaUndo className="me-2" />
                    Restore Selected ({selectedTeachers.size})
                  </>
                )}
              </Button>
            </div>
          </div>
        </Modal.Footer>
      </Modal>

      <Modal show={showConfirmation} onHide={() => setShowConfirmation(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Restoration</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="warning">
            <FaExclamationTriangle className="me-2" />
            <strong>Are you sure?</strong>
          </Alert>
          <p>You are about to restore <strong>{selectedTeachers.size}</strong> teacher(s).</p>
          <p className="mb-0">
            <small className="text-muted">
              Restored teachers will regain access to their accounts and any classes they owned.
            </small>
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowConfirmation(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={confirmRestore}>
            <FaUndo className="me-2" />
            Yes, Restore Now
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default RestoreDeletedTeachersModal;